import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { takeUntil, distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { DestroyService } from './destroy.service';
import { getProfileState } from '@store/profile/profile.selectors';
import { getTimezones } from '@store/timezones/timezones.selectors';
import { ChangeTimezoneModalComponent } from '@shared/modals/change-timezone-modal/change-timezone-modal.component';
import { IState } from '../../store';
import { IUser } from '@interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class DetectBrowserTimezoneService {
  private readonly store: Store<IState> = inject(Store);
  private readonly matDialog: MatDialog = inject(MatDialog);
  private readonly destroy$: DestroyService = inject(DestroyService);

  private readonly timezoneAliases: Readonly<Record<string, string>> = {
    'Europe/Kiev': 'Europe/Kyiv',
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Pacific/Samoa': 'Pacific/Pago_Pago',
    'US/Pacific': 'America/Los_Angeles',
    'US/Eastern': 'America/New_York',
    'Canada/Eastern': 'America/Toronto',
    'Australia/ACT': 'Australia/Sydney',
  };

  private isDialogOpen = false;

  constructor() {
    this.init();
  }

  private init(): void {
    this.store
      .select(getProfileState)
      .pipe(
        map(state => state.data),
        distinctUntilChanged(),
        filter(data => !!data),
        takeUntil(this.destroy$),
      )
      .subscribe(data => this.maybeShowDialog(data));
  }

  private normalizeTimezone(tz: string | null): string | null {
    if (!tz) return null;
    return this.timezoneAliases[tz] ?? tz;
  }

  public getBrowserTimezone(): string | null {
    return this.getBrowserTimezoneInternal();
  }

  private getBrowserTimezoneInternal(): string | null {
    try {
      const tz = Intl?.DateTimeFormat?.().resolvedOptions().timeZone;
      return this.normalizeTimezone(tz || null);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return null;
    }
  }

  private maybeShowDialog(profile: IUser): void {
    if (!profile) return;
    if (this.isDialogOpen) return;

    const browserTz = this.getBrowserTimezoneInternal();
    const userTz = this.normalizeTimezone(profile.timezone || null);

    if (!browserTz || !userTz) return;

    if (browserTz === userTz) return;

    this.isDialogOpen = true;
    this.store
      .select(getTimezones)
      .pipe(
        filter(timezones => Array.isArray(timezones) && timezones.length > 0),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const dialogRef = this.matDialog.open(ChangeTimezoneModalComponent, {
          width: '394px',
          data: { timezone: userTz, browserTimezone: browserTz },
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.isDialogOpen = false;
          });
      });
  }
}
